/**
 * Conectar o WhatsApp da barbearia.
 *
 * A tela existe para uma decisão, não para uma configuração: ligar isto é
 * aceitar que o número do negócio pode ser suspenso. Por isso o aviso vem
 * **antes** do botão, com as três frases inteiras, e a confirmação diz o que
 * está sendo aceito em vez de "OK".
 *
 * O pareamento é por código, não por QR: o dono está com o celular na mão, e um
 * QR na tela do celular exigiria um segundo aparelho para ler.
 */

import { isPairingCodeValid } from '@barbearia/core';
import type { TenantSettingsInput } from '@barbearia/shared';
import {
  BRIDGE_CONSENT_CONFIRMATION,
  BRIDGE_CONSENT_NOTICE,
  type BridgeTokenView,
  WHATSAPP_BRIDGE_STATUS_LABELS,
  type WhatsappBridgeView,
} from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ScreenHeader } from '../../App.tsx';
import { Badge, Button, ErrorNote, Field, Spinner } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { timeOf } from '../../lib/format.ts';
import { ToggleRow } from '../Settings.tsx';

export function WhatsappBridge() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  /**
   * O token em claro, que a API mostra **uma vez**. Fica em estado, e não em
   * cache: um `invalidateQueries` não pode fazê-lo reaparecer, porque ele não
   * existe mais em lugar nenhum.
   */
  const [token, setToken] = useState<BridgeTokenView | null>(null);
  /**
   * Com a ponte da plataforma ligada, a chave não tem uso nenhum na vida do
   * dono — ela só interessa a quem for hospedar a própria conexão. Em destaque,
   * ela faria ele achar que precisa fazer alguma coisa com aquilo, e a resposta
   * certa é "nada": o próximo passo dele é digitar o número e pegar o código.
   *
   * O botão de emitir outra chave fica junto, e por um motivo a mais: ele zera
   * o `lastHeartbeatAt` e o status. Com a plataforma hospedando — onde a chave
   * da barbearia não é usada para nada — tocá-lo faria a tela dizer
   * "Desconectado" por até um minuto sobre uma conexão que não foi interrompida.
   */
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['whatsapp-bridge'],
    queryFn: () => api.get<{ bridge: WhatsappBridgeView }>('/whatsapp'),
    // Enquanto espera o código ou o primeiro sinal, a tela pergunta de novo: é
    // a ponte que responde, e ela responde em segundos.
    refetchInterval: (query) => (query.state.data?.bridge.status === 'CONNECTED' ? 15_000 : 3000),
  });

  const bridge = data?.bridge;

  function fail(failure: unknown) {
    setError(failure instanceof ApiError ? failure : null);
  }

  /**
   * Os dois parâmetros moram aqui, e não em Ajustes › Regras da agenda, porque
   * é aqui que o dono está quando decide o que a conexão guarda. Eles só fazem
   * sentido com a ponte ligada.
   */
  const { data: settings } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => api.get<{ settings: TenantSettingsInput }>('/tenant/settings'),
  });

  const updateSettings = useMutation({
    mutationFn: (patch: Partial<TenantSettingsInput>) => api.patch('/tenant/settings', patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-settings'] }),
    onError: fail,
  });

  const enable = useMutation({
    mutationFn: () =>
      api.post<{ token: BridgeTokenView; bridge: WhatsappBridgeView }>('/whatsapp/enable', {
        consent: true,
      }),
    onSuccess: (result) => {
      setToken(result.token);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-bridge'] });
    },
    onError: fail,
  });

  const rotate = useMutation({
    mutationFn: () =>
      api.post<{ token: BridgeTokenView; bridge: WhatsappBridgeView }>('/whatsapp/token', {}),
    onSuccess: (result) => {
      setToken(result.token);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-bridge'] });
    },
    onError: fail,
  });

  const pair = useMutation({
    mutationFn: () => api.post('/whatsapp/pair', { phone }),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-bridge'] });
    },
    onError: fail,
  });

  const disable = useMutation({
    mutationFn: () => api.post('/whatsapp/disable', {}),
    onSuccess: () => {
      setToken(null);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-bridge'] });
    },
    onError: fail,
  });

  if (isPending || !bridge) {
    return (
      <>
        <ScreenHeader title="WhatsApp" />
        <div className="px-4 pt-6">
          <Spinner />
        </div>
      </>
    );
  }

  const codeValid = isPairingCodeValid(
    bridge.pairingExpiresAt ? new Date(bridge.pairingExpiresAt) : null,
    new Date(),
  );

  return (
    <>
      <ScreenHeader title="WhatsApp" />

      <div className="flex flex-col gap-6 px-4 pt-4 pb-8">
        {error && <ErrorNote>{error.message}</ErrorNote>}

        {!bridge.enabled ? (
          <section className="flex flex-col gap-4">
            <p className="text-[15px] text-ink">
              Conectando o WhatsApp da barbearia, quem te chama aparece na busca de agendar já com
              nome, telefone e foto — e vira cliente no primeiro horário que você marcar.
            </p>

            {/* O aviso vem antes do botão, e inteiro. Ligar isto é decisão sobre
                o telefone do negócio dele, não uma preferência de tela. */}
            <ul className="flex flex-col gap-3 rounded-xl border border-danger/40 bg-danger/5 p-4">
              {BRIDGE_CONSENT_NOTICE.map((line) => (
                <li key={line} className="text-[14px] leading-snug text-ink-soft">
                  {line}
                </li>
              ))}
            </ul>

            <Button full loading={enable.isPending} onClick={() => enable.mutate()}>
              {BRIDGE_CONSENT_CONFIRMATION}
            </Button>
          </section>
        ) : (
          <>
            <section className="flex flex-col gap-2">
              <div className="flex items-center justify-between rounded-xl border border-line bg-surface-raised px-4 py-3">
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium text-ink">
                    {WHATSAPP_BRIDGE_STATUS_LABELS[bridge.status]}
                  </span>
                  <span className="block text-[13px] text-ink-faint">
                    {bridge.linkedPhoneFormatted ?? 'Nenhum número vinculado'}
                  </span>
                </span>
                {bridge.status === 'CONNECTED' && !bridge.signalStale ? (
                  <Badge tone="ok">no ar</Badge>
                ) : bridge.linkedPhone === null ? (
                  // Quem nunca vinculou número não está "sem sinal": está no
                  // começo. O selo vermelho ali é alarme sobre um estado normal,
                  // e some logo que ele parear.
                  <Badge>não vinculado</Badge>
                ) : (
                  <Badge tone="danger">sem sinal</Badge>
                )}
              </div>

              {/* Falha silenciosa é o pior defeito possível aqui: ele procuraria
                  um contato, não acharia, e concluiria que o recurso não presta. */}
              {bridge.signalStale && bridge.status !== 'DISCONNECTED' && (
                <p className="text-[13px] text-danger">
                  {bridge.lastHeartbeatAt
                    ? `Sem sinal desde ${timeOf(bridge.lastHeartbeatAt, tenant.timezone)}. Enquanto isso, contato novo não entra.`
                    : 'A conexão ainda não deu sinal de vida. Confira se ela está rodando.'}
                </p>
              )}

              {bridge.lastError && <p className="text-[13px] text-ink-soft">{bridge.lastError}</p>}

              {/* Só enquanto o formulário de vincular estiver na tela: com o
                  código à mostra, "aqui embaixo" aponta para um campo que foi
                  substituído por ele. */}
              {bridge.platformBridge &&
                bridge.status !== 'CONNECTED' &&
                !(codeValid && bridge.pairingCode) && (
                  <p className="text-[13px] text-ink-soft">
                    A conexão é hospedada pela plataforma: é só vincular o número aqui embaixo.
                  </p>
                )}

              {bridge.draftCount > 0 && (
                <p className="text-[13px] text-ink-faint">
                  {bridge.draftCount} {bridge.draftCount === 1 ? 'contato' : 'contatos'} esperando
                  na busca de agendar.
                </p>
              )}
            </section>

            {token && (!bridge.platformBridge || showAdvanced) && (
              <section className="flex flex-col gap-2 rounded-xl border border-primary bg-primary/10 p-4">
                <h2 className="text-[15px] font-semibold text-ink">Chave da conexão</h2>
                <p className="text-[13px] text-ink-soft">
                  Guarde agora: ela aparece uma vez só. É com ela que a conexão fala com a
                  barbearia.
                </p>
                <code className="block overflow-x-auto rounded-lg bg-surface-sunken px-3 py-2 font-mono text-[13px] break-all text-ink">
                  {token.token}
                </code>
              </section>
            )}

            {codeValid && bridge.pairingCode ? (
              <section className="flex flex-col gap-3 rounded-xl border border-primary bg-primary/10 p-4">
                <h2 className="text-[15px] font-semibold text-ink">Digite este código</h2>
                <p className="font-mono text-[28px] font-bold tracking-[0.2em] text-ink">
                  {bridge.pairingCode}
                </p>
                <p className="text-[13px] leading-snug text-ink-soft">
                  No WhatsApp: Configurações › Aparelhos conectados › Conectar um aparelho ›
                  Conectar com número de telefone.
                </p>
              </section>
            ) : (
              <section className="flex flex-col gap-3">
                <h2 className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
                  {bridge.status === 'CONNECTED' ? 'Vincular outro número' : 'Vincular o número'}
                </h2>
                <Field
                  label="WhatsApp da barbearia"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  inputMode="tel"
                  placeholder="(51) 99999-8888"
                  error={error?.fieldError('phone')}
                  hint="O código chega aqui em alguns segundos"
                />
                <Button
                  full
                  variant="secondary"
                  loading={pair.isPending}
                  disabled={phone.trim().length === 0}
                  onClick={() => pair.mutate()}
                >
                  Pedir o código
                </Button>
                {bridge.status === 'PAIRING' && !codeValid && (
                  <p className="text-[13px] text-ink-faint">Esperando a conexão gerar o código…</p>
                )}
              </section>
            )}

            {settings && (
              <section className="flex flex-col gap-2">
                <h2 className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
                  O que guardar
                </h2>
                <div className="overflow-hidden rounded-xl border border-line">
                  <ToggleRow
                    label="Descartar contato sem foto"
                    hint="Quem não tem foto no WhatsApp é apagado — nome e telefone vão junto"
                    value={settings.settings.deleteContactsWithoutPhoto}
                    onChange={(value) =>
                      updateSettings.mutate({ deleteContactsWithoutPhoto: value })
                    }
                  />
                  <ToggleRow
                    label="Apagar contatos ao desconectar"
                    hint="Desligado, eles esperam a próxima conexão"
                    value={settings.settings.deleteContactsOnDisconnect}
                    onChange={(value) =>
                      updateSettings.mutate({ deleteContactsOnDisconnect: value })
                    }
                  />
                </div>
              </section>
            )}

            <section className="flex flex-col gap-3 border-t border-line pt-5">
              <h2 className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
                Manutenção
              </h2>
              {bridge.platformBridge && !showAdvanced ? (
                <button
                  type="button"
                  onClick={() => setShowAdvanced(true)}
                  className="touch-target text-left text-[13px] font-medium text-accent"
                >
                  Quero hospedar minha própria conexão
                </button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    full
                    loading={rotate.isPending}
                    onClick={() => rotate.mutate()}
                  >
                    Trocar a chave{' '}
                    {bridge.tokenLastFour ? `(termina em ${bridge.tokenLastFour})` : ''}
                  </Button>
                  <p className="text-[12px] text-ink-faint">
                    A chave anterior para de valer na hora, e a conexão cai até você configurá-la
                    com a nova.
                  </p>
                </>
              )}

              <Button
                variant="danger"
                full
                loading={disable.isPending}
                onClick={() => disable.mutate()}
              >
                Desconectar
              </Button>
              {/* A frase segue o interruptor acima: prometer que apaga quando
                  não apaga — ou o contrário — é o tipo de mentira que o dono só
                  descobre depois de tocar no botão. */}
              <p className="text-[12px] text-ink-faint">
                {settings?.settings.deleteContactsOnDisconnect
                  ? 'Desconectar apaga os contatos que ainda não viraram cliente. Quem já é cliente fica.'
                  : 'Os contatos ficam guardados para a próxima conexão. Para apagá-los ao desconectar, use o interruptor acima.'}
              </p>
            </section>
          </>
        )}
      </div>
    </>
  );
}
